import React, { useEffect, useState } from 'react';
import { getQuotes, customerAcceptQuote, customerDeclineQuote, fetchQuotePdfUrl, createQuoteRequest, getSites } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { FileSignature, CheckCircle2, XCircle, Clock, AlertCircle, Download, Eye, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { PdfPreviewDialog } from '../../components/PdfPreviewDialog';
import { usePdfPreview } from '../../hooks/usePdfPreview';

const STATUS_META = {
  draft:     { label: 'Draft',     classes: 'bg-slate-100 text-slate-700 border-slate-200',     icon: Clock },
  sent:      { label: 'Awaiting decision', classes: 'bg-amber-50 text-amber-800 border-amber-200', icon: AlertCircle },
  accepted:  { label: 'Accepted',  classes: 'bg-emerald-50 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
  declined:  { label: 'Declined',  classes: 'bg-red-50 text-red-800 border-red-200',             icon: XCircle },
  converted: { label: 'In progress', classes: 'bg-blue-50 text-blue-800 border-blue-200',        icon: CheckCircle2 },
  expired:   { label: 'Expired',   classes: 'bg-slate-100 text-slate-500 border-slate-200',      icon: Clock },
};

const formatGBP = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CustomerQuotes = () => {
  const { user } = useAuth();
  const pdf = usePdfPreview();
  const onPreview = (q) => pdf.preview(() => fetchQuotePdfUrl(q.id));
  const onDownload = (q) => pdf.download(() => fetchQuotePdfUrl(q.id));
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionFor, setDecisionFor] = useState(null);
  const [decisionType, setDecisionType] = useState('accept');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // "Request a quote" state — a lightweight form so the customer can
  // ask Lutrom to price a new piece of work directly from the Quotes
  // page. Submits a service_request with kind='quote_request'.
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestForm, setRequestForm] = useState({
    title: '', description: '', priority: 'medium', siteId: '',
  });
  const [sites, setSites] = useState([]);
  const [sendingRequest, setSendingRequest] = useState(false);

  const refresh = () => getQuotes().then(setQuotes);
  useEffect(() => {
    refresh().finally(() => setLoading(false));
    // Sites are only used for the "Request a quote" dialog — swallow
    // errors quietly so the main quotes list still renders if the
    // customer's org has no sites yet.
    getSites().then(setSites).catch(() => setSites([]));
  }, []);

  const openRequest = () => {
    setRequestForm({ title: '', description: '', priority: 'medium', siteId: '' });
    setRequestOpen(true);
  };

  const submitRequest = async () => {
    if (!requestForm.title.trim()) {
      toast.error('Give the request a short title so we know what you need.');
      return;
    }
    setSendingRequest(true);
    try {
      await createQuoteRequest({
        title: requestForm.title.trim(),
        description: requestForm.description.trim(),
        priority: requestForm.priority,
        siteId: requestForm.siteId || undefined,
      });
      toast.success('Quote request sent — the Lutrom team will reply with a quote shortly.');
      setRequestOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send request');
    } finally {
      setSendingRequest(false);
    }
  };

  const open = (quote, type) => {
    setDecisionFor(quote);
    setDecisionType(type);
    setNote('');
    setDecisionOpen(true);
  };

  const submit = async () => {
    if (!decisionFor) return;
    setSubmitting(true);
    try {
      if (decisionType === 'accept') {
        await customerAcceptQuote(decisionFor.id, note);
        toast.success('Quote accepted. Our team has been notified and will be in touch shortly.');
      } else {
        await customerDeclineQuote(decisionFor.id, note);
        toast.success('Quote declined. Thanks for letting us know.');
      }
      setDecisionOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit decision');
    } finally {
      setSubmitting(false);
    }
  };

  const visible = quotes.filter(q => q.status !== 'draft');
  const awaiting = visible.filter(q => q.status === 'sent');
  const decided  = visible.filter(q => q.status !== 'sent');

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="customer-quotes-page">
      <PageHeader
        title="Quotes"
        description="Review and accept quotes from Lutrom Engineering. Once you accept we'll schedule the work straight away."
        actions={
          <Button
            onClick={openRequest}
            data-testid="request-quote-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> Request a quote
          </Button>
        }
      />

      {loading && <div className="text-slate-500">Loading quotes...</div>}

      {!loading && visible.length === 0 && (
        <div className="bg-white border border-dashed border-slate-200 rounded-md p-12 text-center">
          <FileSignature className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <div className="font-display uppercase tracking-wide text-slate-700 mb-1">No quotes yet</div>
          <div className="text-sm text-slate-500 mb-4">When we send you a quote it will appear here for review.</div>
          <Button
            onClick={openRequest}
            data-testid="request-quote-empty-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Plus className="h-4 w-4 mr-2" /> Request a quote
          </Button>
        </div>
      )}

      {awaiting.length > 0 && (
        <section data-testid="awaiting-section">
          <h2 className="font-display uppercase tracking-wide text-sm text-slate-700 mb-3">Awaiting your decision</h2>
          <div className="space-y-4">
            {awaiting.map(q => <QuoteCard key={q.id} quote={q} onAccept={() => open(q, 'accept')} onDecline={() => open(q, 'decline')} onPreview={onPreview} onDownload={onDownload} busy={pdf.busy} />)}
          </div>
        </section>
      )}

      {decided.length > 0 && (
        <section data-testid="decided-section">
          <h2 className="font-display uppercase tracking-wide text-sm text-slate-700 mb-3">History</h2>
          <div className="space-y-3">
            {decided.map(q => <QuoteCard key={q.id} quote={q} compact onPreview={onPreview} onDownload={onDownload} busy={pdf.busy} />)}
          </div>
        </section>
      )}

      <Dialog open={decisionOpen} onOpenChange={setDecisionOpen}>
        <DialogContent data-testid="quote-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {decisionType === 'accept' ? `Accept quote ${decisionFor?.id}?` : `Decline quote ${decisionFor?.id}?`}
            </DialogTitle>
            <DialogDescription>
              {decisionType === 'accept'
                ? 'Accepting will notify Lutrom Engineering immediately. We may convert this into a scheduled work order.'
                : 'Declining lets the Lutrom team know this quote is not going ahead. You can always request a revised quote.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-sm text-slate-600">Optional note to the team:</div>
            <Textarea
              data-testid="quote-decision-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={decisionType === 'accept' ? 'e.g. preferred start date, on-site contact...' : 'e.g. budget concerns, alternative supplier...'}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionOpen(false)} disabled={submitting}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={submitting}
              data-testid={decisionType === 'accept' ? 'confirm-accept-btn' : 'confirm-decline-btn'}
              className={decisionType === 'accept'
                ? 'bg-emerald-700 hover:bg-emerald-800 text-white'
                : 'bg-red-600 hover:bg-red-700 text-white'}
            >
              {submitting ? 'Sending...' : decisionType === 'accept' ? 'Accept quote' : 'Decline quote'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request-a-quote dialog. Submits a service_request with
          kind='quote_request' so admins see it in the shared Work
          Requests inbox but tagged as a quote ask. */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent
          className="max-w-lg w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6"
          data-testid="request-quote-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl pr-6">Request a quote</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Tell us what you need priced. The Lutrom team will follow up with a written quote you can accept right here on this page.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                placeholder="e.g. Replace the switchboard in Plant Room 2"
                value={requestForm.title}
                onChange={(e) => setRequestForm({ ...requestForm, title: e.target.value })}
                className="mt-1"
                data-testid="request-quote-title"
                maxLength={140}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Details</Label>
              <Textarea
                placeholder="Scope, timing, any drawings we'll need, access constraints, etc."
                value={requestForm.description}
                onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
                rows={5}
                className="mt-1"
                data-testid="request-quote-description"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Priority</Label>
                <Select
                  value={requestForm.priority}
                  onValueChange={(v) => setRequestForm({ ...requestForm, priority: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="request-quote-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low — no rush</SelectItem>
                    <SelectItem value="medium">Medium — normal</SelectItem>
                    <SelectItem value="high">High — soon please</SelectItem>
                    <SelectItem value="urgent">Urgent — emergency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Site (optional)</Label>
                <Select
                  value={requestForm.siteId || '__none__'}
                  onValueChange={(v) => setRequestForm({ ...requestForm, siteId: v === '__none__' ? '' : v })}
                >
                  <SelectTrigger className="mt-1" data-testid="request-quote-site">
                    <SelectValue placeholder="Pick a site…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {sites.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setRequestOpen(false)}
              disabled={sendingRequest}
            >
              Cancel
            </Button>
            <Button
              onClick={submitRequest}
              disabled={sendingRequest || !requestForm.title.trim()}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="request-quote-submit"
            >
              {sendingRequest ? 'Sending…' : 'Send request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PdfPreviewDialog
        url={pdf.url}
        filename={pdf.filename}
        onClose={pdf.close}
        title="Quote preview"
      />
    </div>
  );
};

const QuoteCard = ({ quote, onAccept, onDecline, compact = false, onPreview, onDownload, busy = false }) => {
  const meta = STATUS_META[quote.status] || STATUS_META.sent;
  const Icon = meta.icon;
  return (
    <div
      data-testid={`quote-card-${quote.id}`}
      className={`bg-white border rounded-md ${compact ? 'border-slate-200' : 'border-slate-300 shadow-sm'} overflow-hidden`}
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-xs font-mono text-slate-500">{quote.id}</span>
          <span className={`inline-flex items-center gap-1 text-xs font-medium border px-2 py-0.5 rounded ${meta.classes}`}>
            <Icon className="h-3 w-3" /> {meta.label}
          </span>
        </div>
        <div className="text-xs text-slate-500">
          {quote.expiryDate && quote.status === 'sent' && <>Valid until <strong>{quote.expiryDate}</strong></>}
          {quote.acceptedAt && quote.status === 'accepted' && <>Accepted <strong>{quote.acceptedAt.split('T')[0]}</strong></>}
          {quote.declinedAt && quote.status === 'declined' && <>Declined <strong>{quote.declinedAt.split('T')[0]}</strong></>}
        </div>
      </div>
      <div className="p-5 space-y-3">
        <div>
          <div className="font-semibold text-slate-900 text-lg">{quote.title}</div>
          {quote.description && <div className="text-sm text-slate-600 mt-1 whitespace-pre-line">{quote.description}</div>}
        </div>

        {!compact && quote.lineItems && quote.lineItems.length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left font-medium pb-1.5">Description</th>
                  <th className="text-right font-medium pb-1.5 w-20">Qty</th>
                  <th className="text-right font-medium pb-1.5 w-24">Unit</th>
                  <th className="text-right font-medium pb-1.5 w-24">Total</th>
                </tr>
              </thead>
              <tbody>
                {quote.lineItems.map((li, idx) => (
                  <tr key={idx} className="border-t border-slate-50">
                    <td className="py-2 text-slate-700">{li.description}</td>
                    <td className="py-2 text-right text-slate-700">{li.quantity}</td>
                    <td className="py-2 text-right text-slate-700">{formatGBP(li.unit_amount)}</td>
                    <td className="py-2 text-right text-slate-900 font-medium">{formatGBP((li.quantity || 1) * (li.unit_amount || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <div className="text-xs text-slate-500">Total inc. parts &amp; labour</div>
          <div className="text-2xl font-display tracking-tight text-slate-900">{formatGBP(quote.amount)}</div>
        </div>

        {quote.customerNote && (
          <div className="text-xs bg-slate-50 border border-slate-100 rounded p-3">
            <span className="font-medium">Your note:</span> {quote.customerNote}
          </div>
        )}

        {quote.status === 'sent' && onAccept && onDecline && (
          <div className="flex gap-2 pt-2">
            <Button
              onClick={onAccept}
              data-testid={`accept-quote-${quote.id}`}
              className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" /> Accept quote
            </Button>
            <Button
              onClick={onDecline}
              variant="outline"
              data-testid={`decline-quote-${quote.id}`}
              className="flex-1 border-red-200 text-red-700 hover:bg-red-50"
            >
              <XCircle className="h-4 w-4 mr-2" /> Decline
            </Button>
          </div>
        )}

        <div className="pt-2 border-t border-slate-100 flex flex-wrap gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPreview?.(quote)}
            disabled={busy}
            data-testid={`preview-quote-${quote.id}`}
            className="text-slate-600 hover:text-slate-900"
          >
            <Eye className="h-4 w-4 mr-2" /> Preview PDF
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDownload?.(quote)}
            data-testid={`download-quote-${quote.id}`}
            className="text-slate-600 hover:text-slate-900"
          >
            <Download className="h-4 w-4 mr-2" /> Download PDF
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerQuotes;
